import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../utils/api';

// ─── CSV helpers ──────────────────────────────────────────────────────────────

/** Parse a CSV string into {headers, rows, totalDataRows} for the preview table. */
function parseCSVPreview(text, maxRows = 5) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    .split('\n').filter(l => l.trim());
  if (!lines.length) return { headers: [], rows: [], totalDataRows: 0 };

  const parseRow = (line) => {
    const fields = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (c === ',' && !inQ) { fields.push(cur.trim()); cur = ''; }
      else cur += c;
    }
    fields.push(cur.trim());
    return fields;
  };

  const headers = parseRow(lines[0]);
  const dataLines = lines.slice(1).filter(l => l.trim());
  const rows = dataLines.slice(0, maxRows).map(parseRow);
  return { headers, rows, totalDataRows: dataLines.length };
}

/** Read a File as text (CSV) or ArrayBuffer→CSV (XLSX). Always resolves to a plain CSV string. */
function convertToCsv(file) {
  return new Promise((resolve, reject) => {
    const isXlsx = /\.(xlsx|xls)$/i.test(file.name);
    const reader  = new FileReader();
    if (!isXlsx) {
      reader.onload  = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.readAsText(file);
    } else {
      reader.onload = (e) => {
        try {
          const wb  = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
          const csv = XLSX.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]]);
          resolve(csv);
        } catch (err) { reject(new Error('Could not parse XLSX: ' + err.message)); }
      };
      reader.onerror = () => reject(new Error('Could not read XLSX file'));
      reader.readAsArrayBuffer(file);
    }
  });
}

function fmt(n) { return Number(n).toLocaleString(); }

const REASON_LABEL = {
  missing_name:    'Missing name',
  missing_email:   'Missing email',
  duplicate_email: 'Duplicate email',
};
function reasonLabel(r) {
  if (REASON_LABEL[r]) return REASON_LABEL[r];
  if (r.startsWith('unrecognised_program')) return 'Unknown program: ' + r.split(': ')[1];
  return r;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function UploadCloudIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
    </svg>
  );
}
function CheckCircleIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#1F8A5B"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  );
}
function AlertTriangleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}
function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function SpinnerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: 'im-spin 0.8s linear infinite' }}>
      <line x1="12" y1="2" x2="12" y2="6"/>
      <line x1="12" y1="18" x2="12" y2="22"/>
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
      <line x1="2" y1="12" x2="6" y2="12"/>
      <line x1="18" y1="12" x2="22" y2="12"/>
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
    </svg>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepDots({ step }) {
  const labels = ['Upload', 'Validate', 'Done'];
  return (
    <div className="im-steps">
      {labels.map((label, i) => (
        <React.Fragment key={label}>
          <div className={`im-step${step === i + 1 ? ' active' : step > i + 1 ? ' done' : ''}`}>
            <div className="im-step-dot">{step > i + 1 ? '✓' : i + 1}</div>
            <span>{label}</span>
          </div>
          {i < labels.length - 1 && (
            <div className={`im-step-line${step > i + 1 ? ' done' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Skip list ────────────────────────────────────────────────────────────────
function SkipList({ skipped, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!skipped.length) return null;
  return (
    <div className="im-skip-block">
      <button className="im-skip-toggle" onClick={() => setOpen(o => !o)}>
        <AlertTriangleIcon />
        <span>{skipped.length} row{skipped.length !== 1 ? 's' : ''} will be skipped</span>
        <span className="im-skip-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="im-skip-table-wrap">
          <table className="im-skip-table">
            <thead>
              <tr><th>Row</th><th>Email</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {skipped.map((s, i) => (
                <tr key={i}>
                  <td>{s.row}</td>
                  <td className="im-skip-email">{s.email || '—'}</td>
                  <td><span className="im-skip-reason">{reasonLabel(s.reason)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Preview table ────────────────────────────────────────────────────────────
function PreviewTable({ previewData }) {
  const { headers, rows, totalDataRows } = previewData;
  if (!headers.length) return null;
  const more = totalDataRows - rows.length;
  return (
    <div className="im-preview-table-wrap">
      <div className="im-preview-table-scroll">
        <table className="im-preview-table">
          <thead>
            <tr>{headers.map((h, i) => <th key={i}>{h || <em style={{color:'#aaa'}}>—</em>}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {headers.map((_, ci) => (
                  <td key={ci}>{row[ci] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more > 0 && (
        <div className="im-preview-more">
          + {more.toLocaleString()} more row{more !== 1 ? 's' : ''} not shown
        </div>
      )}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function ImportStudentsModal({ onClose, onSuccess }) {
  const [step, setStep]           = useState(1);
  const [file, setFile]           = useState(null);       // original File (display only)
  const [csvBlob, setCsvBlob]     = useState(null);       // CSV File blob to upload
  const [previewData, setPreviewData] = useState(null);   // {headers, rows, totalDataRows}
  const [dragging, setDragging]   = useState(false);
  const [converting, setConverting] = useState(false);   // XLSX→CSV in progress
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [preview, setPreview]     = useState(null);       // server dry-run result
  const [result, setResult]       = useState(null);       // server final import result
  const inputRef = useRef();

  // ── File handling ────────────────────────────────────────────────────────────
  const acceptFile = useCallback(async (f) => {
    if (!f) return;
    if (!/\.(csv|xlsx|xls)$/i.test(f.name)) {
      setError('Please upload a .csv or .xlsx file.');
      return;
    }
    setFile(f);
    setError('');
    setPreview(null);
    setCsvBlob(null);
    setPreviewData(null);
    setConverting(true);

    try {
      // Step 1 — client converts XLSX → CSV string (CSV files read as-is)
      const csvText = await convertToCsv(f);

      // Step 2 — client-side preview (instant, no server call)
      setPreviewData(parseCSVPreview(csvText));

      // Step 3 — create a CSV blob to upload (always CSV regardless of source format)
      const csvFileName = f.name.replace(/\.(xlsx|xls)$/i, '.csv');
      setCsvBlob(new File([csvText], csvFileName, { type: 'text/csv' }));
    } catch (err) {
      setError('Could not read file: ' + err.message);
      setFile(null);
    } finally {
      setConverting(false);
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    acceptFile(e.dataTransfer.files[0]);
  }, [acceptFile]);

  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = ()   => setDragging(false);

  // ── Step 1 → 2: send CSV blob to server for dry-run ─────────────────────────
  const runPreview = async () => {
    if (!csvBlob) { setError('Please select a file.'); return; }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', csvBlob);
      const res = await api.post('/students/import-csv?dryRun=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Validation failed. Check file format and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 → 3: send CSV blob again for real import ─────────────────────────
  const runImport = async () => {
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', csvBlob);
      const res = await api.post('/students/import-csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setStep(3);
      onSuccess && onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetToUpload = () => {
    setStep(1); setFile(null); setCsvBlob(null); setPreviewData(null);
    setPreview(null); setError('');
  };

  const handleClose = () => { if (!loading && !converting) onClose(); };

  return (
    <div className="im-overlay" onClick={handleClose}>
      <div className="im-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="im-header">
          <div>
            <div className="im-header-label">Students</div>
            <h2 className="im-header-title">Import from CSV / XLSX</h2>
          </div>
          <button className="im-close" onClick={handleClose} disabled={loading || converting}>
            <XIcon />
          </button>
        </div>

        {/* Step bar */}
        <div className="im-step-bar">
          <StepDots step={step} />
        </div>

        {/* Body */}
        <div className="im-body">

          {/* ── Step 1: Upload + instant client-side preview ───────────────── */}
          {step === 1 && (
            <>
              {/* Drop zone — always visible so user can swap file */}
              <div
                className={`im-drop-zone${dragging ? ' dragging' : ''}${file && !converting ? ' has-file' : ''}`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => !converting && inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={e => acceptFile(e.target.files[0])}
                />
                {converting ? (
                  <>
                    <div className="im-drop-cloud" style={{ color: 'var(--navy)' }}><SpinnerIcon /></div>
                    <div className="im-drop-hint">Converting XLSX to CSV…</div>
                  </>
                ) : file ? (
                  <>
                    <div className="im-drop-filename" style={{ color: '#1F8A5B' }}>✓ {file.name}</div>
                    <div className="im-drop-size">{(file.size / 1024).toFixed(1)} KB — click to replace</div>
                  </>
                ) : (
                  <>
                    <div className="im-drop-cloud"><UploadCloudIcon /></div>
                    <div className="im-drop-hint">
                      <strong>Drop your file here</strong> or click to browse
                    </div>
                    <div className="im-drop-sub">.csv or .xlsx · max 500 rows</div>
                  </>
                )}
              </div>

              {/* Instant client-side preview table */}
              {previewData && previewData.headers.length > 0 && (
                <>
                  <div className="im-preview-label">
                    Preview — {previewData.totalDataRows.toLocaleString()} data row{previewData.totalDataRows !== 1 ? 's' : ''} detected
                  </div>
                  <PreviewTable previewData={previewData} />
                </>
              )}

              {/* Template download */}
              <div className="im-template-row">
                <DownloadIcon />
                <a href="/student-import-template.csv" download className="im-template-link">
                  Download template CSV
                </a>
                <span className="im-template-hint">— same column headers work for .xlsx too</span>
              </div>

              <div className="im-columns-hint">
                <strong>Required:</strong> first_name (or "First Name"), last_name, email &nbsp;·&nbsp;
                <strong>Optional:</strong> phone, campus_login_id, program_code, campus,
                cohort_start_date, practicum_start_date, assigned_pc_email, hours_required,
                doc_first_aid (Y/N), doc_pic (Y/N), doc_immunization (Y/N), doc_resume (Y/N)
              </div>

              {error && <div className="im-error">{error}</div>}
            </>
          )}

          {/* ── Step 2: Server dry-run results ─────────────────────────────── */}
          {step === 2 && preview && (
            <>
              <div className="im-preview-counts">
                <div className="im-count-card green">
                  <div className="im-count-num">{fmt(preview.imported)}</div>
                  <div className="im-count-label">Ready to import</div>
                </div>
                <div className={`im-count-card${preview.skipped.length ? ' amber' : ' muted'}`}>
                  <div className="im-count-num">{fmt(preview.skipped.length)}</div>
                  <div className="im-count-label">Will be skipped</div>
                </div>
                <div className="im-count-card muted">
                  <div className="im-count-num">{fmt(preview.total)}</div>
                  <div className="im-count-label">Total rows in file</div>
                </div>
              </div>

              {preview.imported === 0 && (
                <div className="im-warning-banner">
                  No rows are ready to import. Fix the issues below and re-upload.
                </div>
              )}

              <SkipList skipped={preview.skipped} defaultOpen={preview.imported === 0} />

              {error && <div className="im-error">{error}</div>}
            </>
          )}

          {/* ── Step 3: Done ──────────────────────────────────────────────── */}
          {step === 3 && result && (
            <div className="im-done">
              <CheckCircleIcon />
              <div className="im-done-title">Import complete</div>
              <div className="im-done-sub">
                <strong>{fmt(result.imported)}</strong> student{result.imported !== 1 ? 's' : ''} added successfully
                {result.skipped.length > 0 && ` · ${fmt(result.skipped.length)} skipped`}
              </div>
              <SkipList skipped={result.skipped} defaultOpen />
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="im-footer">
          {step === 1 && (
            <>
              <button className="btn btn-ghost" onClick={handleClose}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={runPreview}
                disabled={!csvBlob || loading || converting}
              >
                {loading ? 'Validating…' : 'Preview & Validate →'}
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button className="btn btn-ghost" onClick={resetToUpload} disabled={loading}>
                ← Back
              </button>
              <button
                className="btn btn-primary"
                onClick={runImport}
                disabled={loading || preview?.imported === 0}
              >
                {loading
                  ? 'Importing…'
                  : `Confirm — Import ${fmt(preview?.imported ?? 0)} student${preview?.imported !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 3 && (
            <button className="btn btn-primary" onClick={handleClose}>Done</button>
          )}
        </div>

      </div>
    </div>
  );
}
